import { Guid } from '@approvalmax/types';
import { miscHelpers, routerHelpers } from '@approvalmax/utils';
import * as common from 'modules/common';
import { findRequestById } from 'modules/common/selectors/requestSelectors';
import { domain, State } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { makeDecision, showOperationResultPopup, showRejectRequestPopup } from '../actions';
import { CompanyRequestListFilter } from '../config';
import { OperationResult } from '../reducers/page/activePopup/operationResultPopupReducer';
import { useSelectedFilter } from './useSelectedFilter';

function validateRequestedApproval(
    me: Guid,
    request: common.selectors.types.ExpandedRequest,
    targetRequestVersion: number,
    step: domain.RequestStep | null
): OperationResult | null {
    if (request.version !== targetRequestVersion) {
        return OperationResult.FailedRequestExternallyUpdated;
    }

    const meParticipant = step && step.participants.find((p) => p.userId === me);

    if (!step || !meParticipant) {
        return OperationResult.FailedNotAnApprover;
    }

    if (step.state === domain.RequestStepState.Done) {
        const approverInfo = step.participants.find(
            (p) => p.decision === domain.RequestStepParticipantDecision.Approve
        );

        if (approverInfo && me !== approverInfo.userId) {
            // Someone has already approved the request on the given step and that was enough (AnyOf rule)
            return OperationResult.FailedRequestStepDone;
        }
    }

    if (step.state === domain.RequestStepState.NotStarted) {
        return OperationResult.FailedRequestStepNotStarted;
    }

    if (meParticipant.decision === domain.RequestStepParticipantDecision.Approve) {
        return OperationResult.FailedRequestAlreadyApproved;
    }

    if (meParticipant.decision === domain.RequestStepParticipantDecision.Reject) {
        return OperationResult.FailedRequestAlreadyRejected;
    }

    return null;
}

const useMakeDecision = (isMobile = false) => {
    const dispatch = useDispatch();
    const { section } = useParams<{ section: string }>();
    const location = useLocation();
    const requestId = routerHelpers.getParamAsString(location.search, 'requestId', '');
    const stepId = routerHelpers.getParamAsString(location.search, 'stepId', '');
    const requestVersion = routerHelpers.getParamAsString(location.search, 'requestVersion', '');
    const decision = routerHelpers.getParamAsString(location.search, 'decision', '');
    const request = useSelector((state: State) => findRequestById(state, requestId));
    const step = request?.steps.find((s) => s.id === stepId) || request?.activeStep || null;
    const selectedFilter = useSelectedFilter();

    const me = useSelector(common.selectors.profile.getProfileUser).id;
    const firstRender = useRef(true);

    useEffect(() => {
        const asyncMakeDecision = async () => {
            if (
                section === CompanyRequestListFilter.CompanyAll &&
                requestVersion &&
                requestId &&
                decision &&
                miscHelpers.isEnumValue(domain.RequestStepParticipantDecision, decision) &&
                decision !== domain.RequestStepParticipantDecision.NoResponse &&
                request &&
                firstRender.current
            ) {
                firstRender.current = false;

                if (isMobile) {
                    if (request) {
                        // Basically, all errors during request load lead to this request-not-found panel
                        dispatch(showOperationResultPopup(requestId, null, OperationResult.FailedRequestNotFound));

                        return;
                    }

                    let failureResult = validateRequestedApproval(me, request, Number(requestVersion), step);

                    if (failureResult) {
                        dispatch(showOperationResultPopup(requestId, step?.id || null, failureResult));
                    } else {
                        dispatch(
                            makeDecision({
                                target: request,
                                decision,
                                selectedFilter,
                                commentText: null,
                                showResultsExplicitly: true,
                                mentionedUserIds: [],
                            })
                        );
                    }
                } else {
                    let failureResult = validateRequestedApproval(me, request, Number(requestVersion), step);

                    if (failureResult) {
                        dispatch(showOperationResultPopup(requestId, step?.id || null, failureResult));
                    } else {
                        if (decision === domain.RequestStepParticipantDecision.Approve) {
                            dispatch(
                                makeDecision({
                                    target: request,
                                    decision: domain.RequestStepParticipantDecision.Approve,
                                    selectedFilter,
                                    commentText: null,
                                    showResultsExplicitly: true,
                                    mentionedUserIds: [],
                                })
                            );
                        } else if (decision === domain.RequestStepParticipantDecision.Reject) {
                            dispatch(showRejectRequestPopup(request.id, true));
                        }
                    }
                }
            }
        };

        asyncMakeDecision();
    }, [request, dispatch, section, decision, me, requestId, requestVersion, step, isMobile, selectedFilter]);
};

export default useMakeDecision;
